/**
 * 付费ODM落地页 - 文案变量集中管理（多语言版本）
 * 所有渲染文案通过 i18next.t() 获取翻译词，英文作为默认值（fallback）。
 * 翻译词 key 定义在 public/lang/local/report-odm.json
 * 路由注册在 public/lang/router.json 的 /odm 条目
 *
 * 【注意】由于 constants.js 在模块加载时执行，而 window.i18next 可能尚未初始化（SSR 服务端无 window），
 * 因此使用 getConstants() 函数延迟获取，组件渲染时调用以确保 i18next 已就绪。
 * SSR 服务端渲染时 i18next.t() 返回英文默认值；客户端 hydrate 后 i18next 已初始化，返回对应语种翻译词。
 *
 * 【纠正说明】必须直接使用 i18next.t('key', 'default') 形式调用，不能使用 t() 等包装函数。
 * 原因：翻译词上报工具 @cckj/i18n-util 的 push.js 通过正则 i18next.t\(\s*['"`]key['"`] 扫描源码匹配翻译词，
 * 如果使用自定义 wrapper 函数名（如 t()），正则无法匹配，导致翻译词无法上报到翻译中心。
 *
 * @module odm/constants
 */

/**
 * SSR 兼容的 i18next 引用：
 * - 客户端：使用 window.i18next（CDN 加载的全局对象，且需已调用 init()）
 * - SSR 服务端（无 window）或 i18next 未初始化：使用 fallback 对象，t() 直接返回第二个参数（英文默认值）
 *
 * 【纠正说明】必须检查 window.i18next.isInitialized，因为 CDN 加载 i18next 库后 window.i18next 即存在，
 * 但 i18next.init() 在 window.js 中异步执行，React 客户端 bundle 可能在 init() 之前执行，
 * 此时 window.i18next.t() 返回 undefined（非 defaultValue），导致页面文案为空。
 * @type {{ t: (key: string, defaultValue: string) => string }}
 */
var i18nextFallback = { t: function(key, defaultValue) { return defaultValue; } };
var i18next = (typeof window !== 'undefined' && window.i18next && window.i18next.isInitialized) ? window.i18next : i18nextFallback;

/**
 * 获取所有文案常量（延迟执行，确保 i18next 已初始化）
 * @returns {{ HERO, COMPARISON, WHO_SHOULD_CHOOSE, ADVANTAGES, STEPS, CTA_BANNER, FAQ, MOBILE_PROMPT, LINKS }}
 */
function getConstants() {
  /**
   * 【纠正说明】SSR Layout（app/web/layout/index.jsx）仅通过 CDN 引入 i18next 库，
   * 但并未引入 public/egg/script/global/window.js（该文件通过 NJK 模板加载，仅非 SSR 页面使用）。
   * 因此 SSR 页面的客户端 hydrate 时 window.i18next 存在但未 init()，t() 返回 undefined。
   * 解决：如果 window.i18next 存在但未初始化，且 window.LNG 有翻译数据，则在此处主动初始化。
   */
  if (typeof window !== 'undefined' && window.i18next && !window.i18next.isInitialized && window.LNG) {
    var lng = (document.cookie.match(/(?:^|;\s*)lng=([^;]*)/) || [])[1] || 'en';
    window.i18next.init({
      lng: lng,
      debug: false,
      resources: { [lng]: { translation: window.LNG } },
      keySeparator: '__keySeparator__',
      nsSeparator: '__nsSeparator__'
    });
  }
  i18next = (typeof window !== 'undefined' && window.i18next && window.i18next.isInitialized) ? window.i18next : i18nextFallback;

  /** Hero Banner 模块 */
  var HERO = {
    trustBadges: [
      i18next.t('odm-hero-badge-1', 'Trusted by 5,000+ sellers'),
      i18next.t('odm-hero-badge-2', 'Verified factories only'),
      i18next.t('odm-hero-badge-3', 'Faster turnaround'),
    ],
    title: i18next.t('odm-hero-title', 'Launch Your Product without Trial and Error –'),
    titleHighlight: i18next.t('odm-hero-title-highlight', 'Premium Paid ODM'),
    subtitle: i18next.t('odm-hero-subtitle', 'Skip unreliable suppliers, slow replies, and costly mistakes. Get a dedicated team to handle sourcing, design, and production for you.'),
    ctaPaid: i18next.t('odm-hero-cta-paid', 'Start My Premium Paid ODM – $100'),
    ctaFree: i18next.t('odm-hero-cta-free', 'Continue with Free ODM'),
  };

  /** Comparison Table 模块 */
  var COMPARISON = {
    title: i18next.t('odm-comparison-title', "Free vs Paid ODM – What's the Difference?"),
    subtitle: i18next.t('odm-comparison-subtitle', "Same platform, different service depth. Pick the plan that matches your brand's stage."),
    serviceLabels: [
      i18next.t('odm-comparison-service-1', 'Dedicated Team'),
      i18next.t('odm-comparison-service-2', 'Response & Follow-Up'),
      i18next.t('odm-comparison-service-3', 'Supply Chain Resources'),
      i18next.t('odm-comparison-service-4', 'After-Sales Support'),
      i18next.t('odm-comparison-service-5', 'Refund Guarantee'),
      i18next.t('odm-comparison-service-6', 'Design & Delivery Scope'),
      i18next.t('odm-comparison-service-7', 'Privacy & Data Security'),
      i18next.t('odm-comparison-service-8', 'Processing SLA'),
    ],
    freeOdm: {
      header: i18next.t('odm-comparison-free-header', 'Free ODM'),
      items: [
        i18next.t('odm-comparison-free-1', 'A randomly assigned agent, no fixed contact'),
        i18next.t('odm-comparison-free-2', 'Long cycles, irregular cadence, and a pace outside your control'),
        i18next.t('odm-comparison-free-3', 'Fewer supplier options, limited choices'),
        i18next.t('odm-comparison-free-4', 'Slow responses, queued ticket handling'),
        i18next.t('odm-comparison-free-5', 'Not applicable'),
        i18next.t('odm-comparison-free-6', 'Basic design & manufacturing'),
        i18next.t('odm-comparison-free-7', 'Standard platform protection'),
        i18next.t('odm-comparison-free-8', 'Best-effort service'),
      ],
    },
    paidOdm: {
      header: i18next.t('odm-comparison-paid-header', 'Premium Paid ODM ($100)'),
      items: [
        { highlight: i18next.t('odm-comparison-paid-1-highlight', 'Sr. Account Manager + Liaison + Designer'), rest: i18next.t('odm-comparison-paid-1-rest', ' – a stable service squad') },
        { highlight: i18next.t('odm-comparison-paid-2-highlight', 'Daily proactive follow-up'), rest: i18next.t('odm-comparison-paid-2-rest', ', transparent and predictable') },
        { highlight: i18next.t('odm-comparison-paid-3-highlight', 'Top-tier vetted suppliers'), rest: i18next.t('odm-comparison-paid-3-rest', ' + multi-option proposals & backups') },
        { highlight: i18next.t('odm-comparison-paid-4-highlight', 'Dedicated CS'), rest: i18next.t('odm-comparison-paid-4-rest', ', fast turnaround, closed-loop handling') },
        { highlight: i18next.t('odm-comparison-paid-5-highlight', 'Refund for unfulfilled portion on quality'), rest: i18next.t('odm-comparison-paid-5-rest', '/delay (>7 days) issues') },
        { highlight: i18next.t('odm-comparison-paid-6-highlight', 'End-to-end'), rest: i18next.t('odm-comparison-paid-6-rest', ': logo, packaging, production, delivery') },
        { highlight: i18next.t('odm-comparison-paid-7-highlight', '100% IP ownership'), rest: i18next.t('odm-comparison-paid-7-rest', ', encrypted storage, strict NDA') },
        { rest: i18next.t('odm-comparison-paid-8-rest', 'All requests reviewed & processed within '), highlight: i18next.t('odm-comparison-paid-8-highlight', '3 business days'), highlightAtEnd: true },
      ],
    },
    footerNote: i18next.t('odm-comparison-footer', 'If you value speed, reliability, and peace of mind – Paid ODM is built for you.'),
    ctaPaid: i18next.t('odm-comparison-cta-paid', 'Start My Premium Paid ODM – $100'),
    ctaFree: i18next.t('odm-comparison-cta-free', 'Continue with Free ODM'),
  };

  /** Who Should Choose 模块 */
  var WHO_SHOULD_CHOOSE = {
    title: i18next.t('odm-who-title', 'Who Should Choose Paid ODM?'),
    items: [
      i18next.t('odm-who-item-1', 'First-time sellers looking to avoid costly mistakes'),
      i18next.t('odm-who-item-2', 'Growing brands that need stable suppliers'),
      i18next.t('odm-who-item-3', 'Anyone tired of slow replies and unclear timelines'),
      i18next.t('odm-who-item-4', 'Sellers who want a team instead of doing everything alone'),
    ],
    ctaPaid: i18next.t('odm-who-cta-paid', 'Start My Premium Paid ODM – $100'),
  };

  /** Main Advantages 模块 */
  var ADVANTAGES = {
    title: i18next.t('odm-advantages-title', 'Main Advantages of Premium Paid ODM'),
    subtitle: i18next.t('odm-advantages-subtitle', 'Built for brand owners who want predictable delivery, protected IP, and risk-free engagement.'),
    sections: [
      {
        title: i18next.t('odm-advantages-section-1-title', 'Professional & Dedicated Support'),
        items: [
          { label: i18next.t('odm-advantages-section-1-item-1-label', 'End-to-end service:'), desc: i18next.t('odm-advantages-section-1-item-1-desc', 'from logo & packaging design to production & delivery.') },
          { label: i18next.t('odm-advantages-section-1-item-2-label', 'Dedicated Manager:'), desc: i18next.t('odm-advantages-section-1-item-2-desc', '1-on-1 expert support throughout your journey.') },
          { label: i18next.t('odm-advantages-section-1-item-3-label', 'Direct Factory Access:'), desc: i18next.t('odm-advantages-section-1-item-3-desc', 'vetted suppliers – best balance of cost, quality, and lead time.') },
        ],
      },
      {
        title: i18next.t('odm-advantages-section-2-title', 'Privacy & Data Security'),
        items: [
          { label: i18next.t('odm-advantages-section-2-item-1-label', 'Full ownership:'), desc: i18next.t('odm-advantages-section-2-item-1-desc', '100% of designs, files & IP belong to you.') },
          { label: i18next.t('odm-advantages-section-2-item-2-label', 'Secure Data:'), desc: i18next.t('odm-advantages-section-2-item-2-desc', 'high-level encryption; archive or delete on request.') },
          { label: i18next.t('odm-advantages-section-2-item-3-label', 'Non-Disclosure:'), desc: i18next.t('odm-advantages-section-2-item-3-desc', 'never shared or promoted without your explicit consent.') },
        ],
      },
      {
        title: i18next.t('odm-advantages-section-3-title', 'Quality Assurance & Refund Policy'),
        items: [
          { label: i18next.t('odm-advantages-section-3-item-1-label', 'Quality Guarantee:'), desc: i18next.t('odm-advantages-section-3-item-1-desc', 'refund for the uncompleted portion if results deviate.') },
          { label: i18next.t('odm-advantages-section-3-item-2-label', 'Timely Delivery:'), desc: i18next.t('odm-advantages-section-3-item-2-desc', 'delays over 7 days → refund eligible') },
          { label: i18next.t('odm-advantages-section-3-item-3-label', 'Fast Processing:'), desc: i18next.t('odm-advantages-section-3-item-3-desc', 'every request handled within 3 business days.') },
        ],
      },
    ],
  };

  /** Steps 模块 */
  var STEPS = {
    title: i18next.t('odm-steps-title', 'With Paid ODM'),
    subtitle: i18next.t('odm-steps-subtitle', 'Same clear workflow – Premium Paid ODM adds a dedicated team, daily follow-up, and a refund safety net at every step'),
    items: [
      { step: i18next.t('odm-steps-step-1', 'STEP 1'), text: i18next.t('odm-steps-text-1', 'Submit your product idea') },
      { step: i18next.t('odm-steps-step-2', 'STEP 2'), text: i18next.t('odm-steps-text-2', 'Your manager handles sourcing and negotiation') },
      { step: i18next.t('odm-steps-step-3', 'STEP 3'), text: i18next.t('odm-steps-text-3', 'Receive samples faster') },
      { step: i18next.t('odm-steps-step-4', 'STEP 4'), text: i18next.t('odm-steps-text-4', 'Confirm production') },
      { step: i18next.t('odm-steps-step-5', 'STEP 5'), text: i18next.t('odm-steps-text-5', 'Launch with confidence') },
    ],
    slogan: {
      text: i18next.t('odm-steps-slogan-text', 'You focus on selling — '),
      highlight: i18next.t('odm-steps-slogan-highlight', 'we handle the rest.'),
    },
  };

  /** CTA Banner 模块 */
  var CTA_BANNER = {
    title: i18next.t('odm-cta-title', 'Stop wasting time on unreliable suppliers.'),
    subtitle: i18next.t('odm-cta-subtitle', 'For just $100, get a dedicated team, faster results, and peace of mind.'),
    cta: i18next.t('odm-cta-button', 'Start My Premium Paid ODM — $100'),
    spo: i18next.t('odm-cta-spo', 'Start Paid ODM — $100'),
  };

  /** FAQ 模块 */
  var FAQ = {
    title: i18next.t('odm-faq-title', 'Frequently Asked Questions'),
    subtitle: i18next.t('odm-faq-subtitle', 'Everything you need to know before choosing between Free Standard ODM and Premium Paid ODM'),
    items: [
      { question: i18next.t('odm-faq-q1', 'What does the $100 Paid ODM include?'), answer: i18next.t('odm-faq-a1', 'A dedicated squad (Senior Account Manager + Liaison + Designer), daily proactive follow-up, premium supplier sourcing, dedicated after-sales support, and a refund guarantee for any unfulfilled portion.') },
      { question: i18next.t('odm-faq-q2', "Can I get a refund if I'm not satisfied?"), answer: i18next.t('odm-faq-a2', 'Yes. If results deviate from the requirements, or if a delay caused by us exceeds 7 days, the unfulfilled portion is eligible for a refund.') },
      { question: i18next.t('odm-faq-q3', 'Will my designs and brand information be leaked?'), answer: i18next.t('odm-faq-a3', 'No. All data is encrypted, IP is 100% owned by you, and your project will never be shared or promoted without your explicit consent.') },
      { question: i18next.t('odm-faq-q4', 'How quickly will I receive the first response?'), answer: i18next.t('odm-faq-a4', 'Every request is reviewed and processed within 3 business days. Paid ODM customers also receive daily proactive follow-up.') },
      { question: i18next.t('odm-faq-q5', 'Can I switch from Free Standard ODM to Paid ODM later?'), answer: i18next.t('odm-faq-a5', 'Absolutely. You can upgrade to Paid ODM at any time, and your dedicated service will continue for the remaining project lifecycle.') },
      { question: i18next.t('odm-faq-q6', 'Why should I pay if Free ODM is available?'), answer: i18next.t('odm-faq-a6', 'Because Paid ODM saves you time, reduces risk, and gives you a dedicated team to handle everything.') },
      { question: i18next.t('odm-faq-q7', 'What can I expect in the first 7 days?'), answer: i18next.t('odm-faq-a7', 'Supplier matching, initial quotations, and sampling plan.') },
    ],
  };

  /** Mobile 端提示文案 */
  var MOBILE_PROMPT = {
    title: i18next.t('odm-mobile-prompt-title', 'Notice'),
    message: i18next.t('odm-mobile-prompt-message', 'Thank you for your support. This feature is currently available on the Web. Go try it out!'),
    buttonText: i18next.t('odm-mobile-prompt-button', 'OK'),
  };

  return {
    HERO: HERO,
    COMPARISON: COMPARISON,
    WHO_SHOULD_CHOOSE: WHO_SHOULD_CHOOSE,
    ADVANTAGES: ADVANTAGES,
    STEPS: STEPS,
    CTA_BANNER: CTA_BANNER,
    FAQ: FAQ,
    MOBILE_PROMPT: MOBILE_PROMPT,
  };
}

/** 跳转路径配置（不需要翻译） */
var LINKS = {
  paidOdm: '/mine/customization/requestForm?paid=customization',
  freeOdm: '/mine/customization/requestForm',
};

module.exports = {
  getConstants: getConstants,
  LINKS: LINKS,
};
